<div class="analytics-container bg-gray-50 min-h-screen">
  <!-- Hero Section -->
  <section class="bg-gradient-to-r from-immovea-navy via-blue-800 to-blue-900 text-white py-8 sm:py-12">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 class="text-3xl sm:text-5xl font-bold mb-2">Analytics Dashboard</h1>
          <p class="text-base sm:text-xl text-blue-100">Comprehensive insights and statistics</p>
        </div>
        <button 
          (click)="goBack()"
          class="bg-white bg-opacity-20 hover:bg-opacity-30 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg transition font-medium flex items-center gap-2 text-sm sm:text-base">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
          </svg>
          Back to Dashboard
        </button>
      </div>
    </div>
  </section>

  <div class="max-w-7xl mx-auto py-8 sm:py-12 px-4 sm:px-6 lg:px-8">
    <!-- Loading State -->
    <div *ngIf="loadingStats()" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
      <div *ngFor="let i of [1,2,3,4]" class="bg-white rounded-lg shadow-md p-6 animate-pulse">
        <div class="h-4 bg-gray-200 rounded w-2/3 mb-4"></div>
        <div class="h-8 bg-gray-200 rounded w-1/2 mb-2"></div>
        <div class="h-3 bg-gray-200 rounded w-3/4"></div>
      </div>
    </div>

    <!-- Key Statistics -->
    <div *ngIf="!loadingStats() && stats()" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
      <!-- Total Properties -->
      <div class="bg-white rounded-lg shadow-md p-6 border-t-4 border-blue-500">
        <div class="flex items-center justify-between mb-2">
          <h3 class="text-sm font-medium text-gray-600 uppercase">Total Properties</h3>
          <svg class="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"></path>
          </svg>
        </div>
        <p class="text-3xl font-bold text-gray-900">{{ stats()!.totalProperties }}</p>
        <p class="text-sm text-gray-500 mt-1">{{ stats()!.publishedProperties }} published, {{ stats()!.draftProperties }} drafts</p>
      </div>

      <!-- Total Messages -->
      <div class="bg-white rounded-lg shadow-md p-6 border-t-4 border-purple-500">
        <div class="flex items-center justify-between mb-2">
          <h3 class="text-sm font-medium text-gray-600 uppercase">Total Messages</h3>
          <svg class="w-5 h-5 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
          </svg>
        </div>
        <p class="text-3xl font-bold text-gray-900">{{ stats()!.totalMessages }}</p>
        <p class="text-sm text-gray-500 mt-1">
          <span class="text-orange-600 font-medium">{{ stats()!.unansweredMessages }}</span> pending response
        </p>
      </div>

      <!-- Today's Messages -->
      <div class="bg-white rounded-lg shadow-md p-6 border-t-4 border-green-500">
        <div class="flex items-center justify-between mb-2">
          <h3 class="text-sm font-medium text-gray-600 uppercase">Today's Messages</h3>
          <svg class="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
          </svg>
        </div>
        <p class="text-3xl font-bold text-gray-900">{{ stats()!.todayMessages }}</p>
        <p class="text-sm text-gray-500 mt-1">New inquiries received today</p>
      </div>

      <!-- Featured Properties -->
      <div class="bg-white rounded-lg shadow-md p-6 border-t-4 border-yellow-500">
        <div class="flex items-center justify-between mb-2">
          <h3 class="text-sm font-medium text-gray-600 uppercase">Featured</h3>
          <svg class="w-5 h-5 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        </div>
        <p class="text-3xl font-bold text-gray-900">{{ stats()!.featuredProperties }}</p>
        <p class="text-sm text-gray-500 mt-1">Highlighted listings</p>
      </div>
    </div>

    <!-- Charts Section -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-12">
      <!-- Property Types Distribution -->
      <div class="bg-white rounded-lg shadow-md p-6">
        <h3 class="text-xl font-bold text-gray-900 mb-6">Property Types</h3>
        
        <div *ngIf="loadingCharts()" class="space-y-4">
          <div *ngFor="let i of [1,2,3]" class="animate-pulse">
            <div class="h-4 bg-gray-200 rounded w-1/4 mb-2"></div>
            <div class="h-8 bg-gray-200 rounded"></div>
          </div>
        </div>

        <div *ngIf="!loadingCharts()" class="space-y-4">
          <div *ngIf="propertyTypeStats().length === 0" class="text-center py-8 text-gray-500">
            No property data available
          </div>
          
          <div *ngFor="let stat of propertyTypeStats()" class="space-y-2">
            <div class="flex items-center justify-between text-sm">
              <span class="font-medium text-gray-700">{{ stat.type }}</span>
              <span class="text-gray-600">{{ stat.count }} ({{ stat.percentage }}%)</span>
            </div>
            <div class="w-full bg-gray-200 rounded-full h-3">
              <div 
                class="bg-blue-600 h-3 rounded-full transition-all duration-500"
                [style.width.%]="stat.percentage">
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Listing Types (Sale/Rent) -->
      <div class="bg-white rounded-lg shadow-md p-6">
        <h3 class="text-xl font-bold text-gray-900 mb-6">Listing Types</h3>
        
        <div *ngIf="loadingCharts()" class="space-y-4">
          <div *ngFor="let i of [1,2]" class="animate-pulse">
            <div class="h-4 bg-gray-200 rounded w-1/4 mb-2"></div>
            <div class="h-8 bg-gray-200 rounded"></div>
          </div>
        </div>

        <div *ngIf="!loadingCharts()" class="space-y-4">
          <div *ngIf="listingTypeStats().length === 0" class="text-center py-8 text-gray-500">
            No listing data available
          </div>
          
          <div *ngFor="let stat of listingTypeStats()" class="space-y-2">
            <div class="flex items-center justify-between text-sm">
              <span class="font-medium text-gray-700">{{ stat.type }}</span>
              <span class="text-gray-600">{{ stat.count }} ({{ stat.percentage }}%)</span>
            </div>
            <div class="w-full bg-gray-200 rounded-full h-3">
              <div 
                class="bg-purple-600 h-3 rounded-full transition-all duration-500"
                [style.width.%]="stat.percentage">
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Message Trends -->
    <div class="bg-white rounded-lg shadow-md p-6 mb-12">
      <div class="flex items-center justify-between mb-6">
        <h3 class="text-xl font-bold text-gray-900">Message Trends (Last 7 Days)</h3>
        <div *ngIf="!loadingCharts() && averageResponseTime() > 0" class="text-sm">
          <span class="text-gray-600">Avg Response Time:</span>
          <span class="font-bold text-blue-600 ml-2">{{ averageResponseTime() }}h</span>
        </div>
      </div>

      <div *ngIf="loadingCharts()" class="h-64 flex items-end justify-between gap-4">
        <div *ngFor="let i of [1,2,3,4,5,6,7]" class="flex-1 bg-gray-200 rounded-t animate-pulse" [style.height.%]="50 + (i * 5)"></div>
      </div>

      <div *ngIf="!loadingCharts()" class="h-64">
        <div *ngIf="messageTrends().length === 0" class="flex items-center justify-center h-full text-gray-500">
          No message data available
        </div>

        <div *ngIf="messageTrends().length > 0" class="h-full flex items-end justify-between gap-2 sm:gap-4">
          <div *ngFor="let trend of messageTrends()" class="flex-1 flex flex-col items-center gap-2">
            <div class="w-full flex items-end justify-center relative group">
              <div 
                class="w-full bg-gradient-to-t from-blue-600 to-blue-400 rounded-t transition-all duration-500 hover:from-blue-700 hover:to-blue-500 cursor-pointer"
                [style.height.%]="getBarHeight(trend.count)"
                [style.min-height.px]="trend.count > 0 ? 20 : 0">
              </div>
              <!-- Tooltip -->
              <div class="absolute bottom-full mb-2 hidden group-hover:block bg-gray-900 text-white text-xs px-2 py-1 rounded whitespace-nowrap z-10">
                {{ trend.count }} message{{ trend.count !== 1 ? 's' : '' }}
              </div>
            </div>
            <div class="text-xs text-gray-600 text-center">
              {{ formatDate(trend.date) }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Additional Stats -->
    <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
      <div class="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg shadow-md p-6 border-l-4 border-blue-600">
        <h4 class="text-sm font-medium text-blue-900 uppercase mb-2">Response Rate</h4>
        <p class="text-3xl font-bold text-blue-900" *ngIf="stats()">
          {{ stats()!.totalMessages > 0 ? ((stats()!.totalMessages - stats()!.unansweredMessages) / stats()!.totalMessages * 100).toFixed(0) : 0 }}%
        </p>
        <p class="text-sm text-blue-700 mt-1">Messages answered</p>
      </div>

      <div class="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg shadow-md p-6 border-l-4 border-purple-600">
        <h4 class="text-sm font-medium text-purple-900 uppercase mb-2">Published Rate</h4>
        <p class="text-3xl font-bold text-purple-900" *ngIf="stats()">
          {{ stats()!.totalProperties > 0 ? (stats()!.publishedProperties / stats()!.totalProperties * 100).toFixed(0) : 0 }}%
        </p>
        <p class="text-sm text-purple-700 mt-1">Properties live</p>
      </div>

      <div class="bg-gradient-to-br from-green-50 to-green-100 rounded-lg shadow-md p-6 border-l-4 border-green-600">
        <h4 class="text-sm font-medium text-green-900 uppercase mb-2">Featured Rate</h4>
        <p class="text-3xl font-bold text-green-900" *ngIf="stats()">
          {{ stats()!.publishedProperties > 0 ? (stats()!.featuredProperties / stats()!.publishedProperties * 100).toFixed(0) : 0 }}%
        </p>
        <p class="text-sm text-green-700 mt-1">Of published properties</p>
      </div>
    </div>
  </div>
</div>
